import { bus } from './EventBus.js';
import S from './Registry.js';
import { proofSnapshot } from './RunScoreProof.js';
import { createAcceptedShareCard } from './Portal.js';
import { sharePostText, sharePostContent } from './SharePost.js';
import './social.css';

export function xIntent(url, score, live = false, verified = false) {
  // Hosted releases cannot open popups and do not ship an X integration.
  if (import.meta.env.VITE_SOCIAL_ENABLED !== 'true') throw new Error('公開サイトで共有してください。');
  const u = new URL(url);
  if (u.protocol !== 'https:' && !(u.protocol === 'http:' && ['localhost','127.0.0.1'].includes(u.hostname))) throw new Error('共有URLが無効です');
  const intent = new URL('https://x.com/intent/tweet');
  intent.searchParams.set('text', sharePostText(score, {live, verified}));
  intent.searchParams.set('url', u.href);
  return intent.href;
}

export function initSocialGrowth() {
  const standalone = window.parent === window;
  const service = standalone && import.meta.env.VITE_SOCIAL_ENABLED === 'true';
  let consent = false, session = null, pendingSession = null, run = crypto.randomUUID().replaceAll('-','');
  let snapshot = null, generation = 0, hasStarted = false;
  const attribution = new URLSearchParams(location.search);
  const post = async (endpoint, body) => {
    const response = await fetch(`/api/social/${endpoint}`, {method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify(body),signal:AbortSignal.timeout(8000)});
    if (!response.ok) throw new Error('共有サービスに接続できません。時間をおいてお試しください。');
    return response.json();
  };
  const track = async (name, runId = run) => {
    if (!service || !consent) return;
    try {
      if (!session) {
        pendingSession ||= post('session',{consent:true,ref:attribution.get('ref'),campaign:attribution.get('utm_campaign')});
        session = await pendingSession;
      }
      if (consent) await post('event',{token:session.token,name,runId});
    } catch { session = null; pendingSession = null; } // Analytics never blocks play or sharing.
  };
  if (service) {
    const control = document.createElement('label'); control.className = 'ax-growth-consent';
    const check = document.createElement('input'); check.type = 'checkbox';
    control.append(check, document.createTextNode('利用計測に協力（任意）'));
    const privacy = document.createElement('a'); privacy.href='./privacy.html'; privacy.target='_blank'; privacy.rel='noopener noreferrer'; privacy.textContent='詳細'; privacy.style.color='inherit';
    control.append(privacy);
    control.title = '同意したセッション・プレイ・共有操作と紹介元を集計します。Xアカウントや投稿内容は取得しません。チェックを外すと送信を止めます。';
    check.addEventListener('change', () => { consent = check.checked; if (consent) void track('visit','0'.repeat(32)); else {session=null;pendingSession=null;} });
    document.body.append(control);
  }
  const dialog = document.createElement('dialog'); dialog.className = 'ax-social-dialog';
  const title = document.createElement('h2'); title.textContent = 'この記録をシェア';
  const status = document.createElement('p'); status.setAttribute('role','status');
  const prepare = document.createElement('button'); prepare.textContent = '共有リンクを作る';
  const x = document.createElement('a'); x.textContent = 'Xでスコアを自慢する'; x.target='_blank'; x.rel='noopener noreferrer'; x.hidden=true;
  const copy = document.createElement('button'); copy.textContent='リンクをコピー'; copy.hidden=true;
  const address = document.createElement('input'); address.readOnly=true; address.hidden=true; address.setAttribute('aria-label','共有URL');
  const postText = document.createElement('textarea'); postText.readOnly=true; postText.hidden=true; postText.rows=3; postText.setAttribute('aria-label','X投稿用の文章とURL');
  const copyPost = document.createElement('button'); copyPost.textContent='投稿文とリンクをコピー'; copyPost.hidden=true;
  address.addEventListener('click',()=>address.select());
  const close = document.createElement('button'); close.textContent='ゲームに戻る';
  const note = document.createElement('p'); note.className='ax-social-note'; note.textContent='投稿はXで確認。Gold TicketはTRAIDENT側で判定します。';
  dialog.append(title,status,prepare,x,address,copy,postText,copyPost,note,close); document.body.append(dialog);
  close.onclick = () => dialog.close();
  dialog.addEventListener('keydown', e => e.stopPropagation());
  dialog.addEventListener('click', e => { if(e.target===dialog) dialog.close(); });
  let shareUrl = null;
  bus.on('ui:shareScore', () => {
    snapshot = Object.freeze(proofSnapshot(S.state));
    title.textContent=`${snapshot.score}桁の記録をシェア`;
    generation++; shareUrl=null; prepare.disabled=false; prepare.hidden=false; x.hidden=true;copy.hidden=true;address.hidden=true;postText.hidden=true;copyPost.hidden=true;
    status.textContent=`${snapshot.score}桁 · WAVE ${snapshot.wave} · ${snapshot.kills}体撃破`;
    dialog.showModal();
  });
  prepare.onclick = async () => {
    const current = generation; const score = snapshot.score;
    prepare.disabled=true; status.textContent='共有リンクを作成中…';
    try {
      const hosted = await createAcceptedShareCard();
      if (!hosted && !standalone) throw new Error('ランキングの記録受理後に共有できます。');
      const incomingCampaign = attribution.get('utm_campaign') || 'organic';
      const campaign = /^[a-zA-Z0-9_-]{1,48}$/.test(incomingCampaign) ? incomingCampaign : 'organic';
      const card = hosted || (service ? await post('share',{score,wave:snapshot.wave,kills:snapshot.kills,campaign}) : null);
      if (!card) throw new Error('共有サービスは公開準備中です。');
      if (current !== generation) return;
      shareUrl = card.shareUrl || card.url;
      const parsed = new URL(shareUrl);
      if(parsed.protocol !== 'https:' && !(parsed.protocol==='http:' && ['localhost','127.0.0.1'].includes(parsed.hostname))) throw new Error('共有URLが無効です');
      const context = {live:hosted?.live === true, verified:!!hosted};
      if(service){ x.href=xIntent(shareUrl,score,context.live,context.verified); x.hidden=false; }
      postText.value=sharePostContent(shareUrl,score,context);postText.hidden=false;copyPost.hidden=false;
      address.value=shareUrl;address.hidden=false;copy.hidden=false;prepare.hidden=true;
      status.textContent=hosted ? '受理済み記録の共有リンクです。' : '個人記録（公開ランキング未認定）';
    } catch(e) { if(current===generation) status.textContent=e.message; }
    finally { if(current===generation) prepare.disabled=false; }
  };
  x.onclick = () => { void track('share_intent'); };
  copy.onclick = async () => { try {await navigator.clipboard.writeText(shareUrl);status.textContent='リンクをコピーしました';void track('copy_link');} catch {address.focus();address.select();status.textContent='URLを選択しました。コピーしてXの投稿欄へ貼り付けてください。';} };
  copyPost.onclick = async () => { try {await navigator.clipboard.writeText(postText.value);status.textContent='投稿文とリンクをコピーしました';void track('copy_link');} catch {postText.focus();postText.select();status.textContent='投稿文とURLを選択しました。コピーしてXへ貼り付けてください。';} };
  const start = () => { generation++;dialog.close();run=crypto.randomUUID().replaceAll('-','');snapshot=null;hasStarted=false; };
  bus.on('ui:start',start); bus.on('ui:restart',start);
  bus.on('state:phase',({phase})=>{if(phase==='playing'&&!hasStarted){hasStarted=true;void track('start');}});
  bus.on('player:died', () => {void track('complete');});
}
